import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOpenAndReturn, useReturnFocus } from '@chess/api-navigation/react';
import type { PiletApi } from '@chess/platform-web';
import type { MoveKind } from '@chess/api-model';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { DASHBOARD_SECTION, registerCard, type SectionParams } from '@chess/api-dashboard';
import { HELP_API, type Help } from '@chess/api-help';
import { memoisedMotifs, MOTIFS_API, type Motifs } from '@chess/api-mistakes';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import '@chess/api-insights/charts.css';
import { MistakesAggregator } from './MistakesAggregator.ts';
import { MistakeGroupsAggregator } from './MistakeGroupsAggregator.ts';
import { cardMeta, MistakeCard } from './MistakeCard.tsx';
import { MistakesTable } from './MistakesTable.tsx';
import { CARD_IDS, cardHelp, helpId } from './help.tsx';
import { parseTableState, writeTableState, type TableState } from './table.ts';
import './mistakes.css';

// The Blunders and Misses screens' content, one kind each from the same
// code. In the screen's 30-day band (its `<screen>.period` slot, the
// `dashboard.band` under the top bar), a `dashboard.section` (5) of
// six cards on `mistakes.<kind>.cards`, two rows of three (each registered
// with `registerCard`, its numbers from `MistakeGroupsAggregator`, its help
// topic `mistakes.<kind>.<card>`), and in the section's `after` slot
// (`mistakes.<kind>.after`, the cards' width) the table of every one, with
// its chips, order and paging in the address (`table.ts`). The screens
// (`screen-blunders`, `screen-misses`) own the pages and their slots; this
// plugin fills them. The motif of each mistake (the cards' families, the
// chips' variations, the table's Pattern column) comes from the
// `mistakes.motifs` service, awaited before the aggregators are registered
// and memoised per fact (`memoisedMotifs`), so a mistake is classified once
// however often the store publishes, never in render.

interface Screen {
  kind: MoveKind;
  /** The screen's band slot, `<screen>.period` (the `dashboard.band` the screen puts under its top bar). */
  slot: string;
}

const SCREENS: Screen[] = [
  { kind: 'blunder', slot: 'blunders.period' },
  { kind: 'miss', slot: 'misses.period' },
];

const ORDER = { patterns: 10, severity: 20, standing: 30, clock: 40, material: 50, weeks: 60 } as const;

/** The table's state in the address: replaced, not pushed, so Back still leaves the screen and comes back to the same view. */
function useTableState(): [TableState, (s: TableState) => void] {
  const [params, setParams] = useSearchParams();
  const state = React.useMemo(() => parseTableState(params), [params]);
  const set = React.useCallback((s: TableState) => setParams((p) => writeTableState(p, s), { replace: true, preventScrollReset: true }), [setParams]);
  return [state, set];
}

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  void app.getApi<Help>(HELP_API).then((help) => {
    for (const { kind } of SCREENS) for (const card of CARD_IDS) help.register(helpId(kind, card), cardHelp(kind, card));
  });

  // The classifier, bound once the service is at hand; the aggregators are registered then, and the cards show their skeletons meanwhile.
  const motifs = app.getApi<Motifs>(MOTIFS_API).then((m) => memoisedMotifs(m));

  for (const { kind, slot } of SCREENS) {
    // The aggregators exist at once (the cards read them by id) and run only once registered, when the classifier is bound.
    let motifOf: ReturnType<typeof memoisedMotifs> | undefined;
    const groups = new MistakeGroupsAggregator(kind, (m, reader) => motifOf!(m, reader));
    const rows = new MistakesAggregator(kind, (m, reader) => motifOf!(m, reader));
    void motifs.then((f) => {
      motifOf = f;
      registerAggregator(getApi, groups);
      registerAggregator(getApi, rows);
    });
    const cardsSlot = `mistakes.${kind}.cards`;
    const afterSlot = `mistakes.${kind}.after`;

    for (const card of CARD_IDS) {
      const Card: React.FC = () => {
        const { result, state } = useAggregate(getApi, groups);
        const navigate = useNavigate();
        const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
        // Never hidden while the store loads: the card shows its skeleton.
        if (state?.status !== 'ready') return state?.status === 'failed' ? null : <MistakeCard kind={kind} card={card} loading />;
        return result ? <MistakeCard kind={kind} card={card} groups={result} link={link} /> : null;
      };
      registerCard(app, cardsSlot, Card, { order: ORDER[card], ...cardMeta(kind, card) });
    }

    const Table: React.FC = () => {
      const openAndReturn = useOpenAndReturn();
      const [tableState, setTableState] = useTableState();
      const { result, state } = useAggregate(getApi, rows);
      // Back from a row's review: the row comes back to the middle of the screen, highlighted.
      useReturnFocus(state?.status === 'ready' && !!result);
      if (state?.status === 'failed') return null;
      if (state?.status !== 'ready' || !result) return <MistakesTable kind={kind} loading />;
      return <MistakesTable kind={kind} mistakes={result.mistakes} state={tableState} onState={setTableState} onOpen={openAndReturn} />;
    };
    app.registerExtension(afterSlot, Table, { order: 10 });

    const section: SectionParams = { slot: cardsSlot, maxColumns: 3, after: afterSlot };
    app.registerExtension(slot, () => <app.Extension name={DASHBOARD_SECTION} params={section} />, { order: 5 });
  }
}
